/**
 * purchaseListEngine.js — Rê Leve (Painel de Operação)
 *
 * Gera lista de compras agregada por ingrediente, baseada nos pedidos
 * (confirmados + previsão) para 3 horizontes:
 *   - amanhã
 *   - esta semana (próximos 7 dias)
 *   - próxima semana (dias 8–14)
 *
 * Modelo: kit → ingredientes por unidade. Multiplica pela demanda esperada.
 * Quando vier dado real de pedidos, o motor usa as quantidades reais.
 */

import { getOrders } from './insightsEngine';

// ── Mapping kit → ingredientes por unidade ─────────────────────────────────
// Quantidade por marmita (1 unidade do kit). Em produção, viria de uma
// tabela técnica em Postgres ou de uma ficha técnica do chef.
const KIT_INGREDIENTS = {
  KIT_001: [ // Dia Leve
    { name: 'Frango grelhado',          qty: 0.180, unit: 'kg', category: 'proteina',     supplier: 'Açougue Boa Viagem' },
    { name: 'Arroz integral',           qty: 0.120, unit: 'kg', category: 'carboidrato',  supplier: 'Mercadão' },
    { name: 'Mix legumes (cenoura+vagem)', qty: 0.100, unit: 'kg', category: 'vegetal',  supplier: 'Hortifruti Pina' },
    { name: 'Salada verde',             qty: 0.060, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Embalagem alumínio M',     qty: 1,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
  ],
  KIT_002: [ // Dia Controle (low-cal)
    { name: 'Peito de frango grelhado', qty: 0.150, unit: 'kg', category: 'proteina',    supplier: 'Açougue Boa Viagem' },
    { name: 'Quinoa',                    qty: 0.080, unit: 'kg', category: 'carboidrato', supplier: 'Mundo Verde' },
    { name: 'Brócolis',                  qty: 0.090, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Beterraba',                 qty: 0.060, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Azeite de oliva',           qty: 0.010, unit: 'l',  category: 'tempero',    supplier: 'Mercadão' },
    { name: 'Embalagem alumínio M',      qty: 1,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
  ],
  KIT_003: [ // Dia Performance (mais proteína)
    { name: 'Peito de frango grelhado',  qty: 0.220, unit: 'kg', category: 'proteina',    supplier: 'Açougue Boa Viagem' },
    { name: 'Batata-doce',                qty: 0.150, unit: 'kg', category: 'carboidrato', supplier: 'Hortifruti Pina' },
    { name: 'Arroz integral',             qty: 0.080, unit: 'kg', category: 'carboidrato', supplier: 'Mercadão' },
    { name: 'Brócolis',                   qty: 0.100, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Ovo cozido',                 qty: 1,     unit: 'un', category: 'proteina',   supplier: 'Granja Pina' },
    { name: 'Embalagem alumínio G',       qty: 1,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
  ],
  KIT_004: [ // Combo Semana (= 5x Kit Dia Leve)
    { name: 'Frango grelhado',          qty: 0.900, unit: 'kg', category: 'proteina',    supplier: 'Açougue Boa Viagem' },
    { name: 'Arroz integral',           qty: 0.600, unit: 'kg', category: 'carboidrato', supplier: 'Mercadão' },
    { name: 'Mix legumes',              qty: 0.500, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Salada verde',             qty: 0.300, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Embalagem alumínio M',     qty: 5,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
    { name: 'Sacola térmica kraft',     qty: 1,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
  ],
  KIT_005: [ // Veg
    { name: 'Tofu',                      qty: 0.120, unit: 'kg', category: 'proteina',    supplier: 'Mundo Verde' },
    { name: 'Grão-de-bico cozido',       qty: 0.100, unit: 'kg', category: 'proteina',    supplier: 'Mundo Verde' },
    { name: 'Arroz integral',            qty: 0.120, unit: 'kg', category: 'carboidrato', supplier: 'Mercadão' },
    { name: 'Abóbora cabotiá',           qty: 0.150, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Embalagem alumínio M',      qty: 1,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
  ],
  KIT_006: [ // Equilíbrio (macarrão integral)
    { name: 'Macarrão parafuso integral', qty: 0.100, unit: 'kg', category: 'carboidrato', supplier: 'Mercadão' },
    { name: 'Frango desfiado',            qty: 0.150, unit: 'kg', category: 'proteina',    supplier: 'Açougue Boa Viagem' },
    { name: 'Molho branco light',         qty: 0.080, unit: 'l',  category: 'molho',      supplier: 'Mercadão' },
    { name: 'Tomate cereja',              qty: 0.040, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Queijo parmesão ralado',     qty: 0.015, unit: 'kg', category: 'laticinio',  supplier: 'Mercadão' },
    { name: 'Embalagem alumínio M',       qty: 1,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
  ],
  KIT_007: [ // Sopa Abóbora
    { name: 'Abóbora cabotiá',           qty: 0.250, unit: 'kg', category: 'vegetal',    supplier: 'Hortifruti Pina' },
    { name: 'Caldo de legumes natural',  qty: 0.200, unit: 'l',  category: 'molho',      supplier: 'cozinha' },
    { name: 'Coentro fresco',            qty: 0.005, unit: 'kg', category: 'tempero',    supplier: 'Hortifruti Pina' },
    { name: 'Pote sopa 300ml',           qty: 1,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
  ],
  KIT_008: [ // Bolinho banana
    { name: 'Banana madura',             qty: 0.080, unit: 'kg', category: 'fruta',      supplier: 'Hortifruti Pina' },
    { name: 'Aveia em flocos',           qty: 0.040, unit: 'kg', category: 'carboidrato', supplier: 'Mundo Verde' },
    { name: 'Ovo',                       qty: 0.3,   unit: 'un', category: 'proteina',   supplier: 'Granja Pina' },
    { name: 'Forminha papel',            qty: 1,     unit: 'un', category: 'embalagem',  supplier: 'Embalax' },
  ],
};

// Ingredientes para os addons (suco, sopa, ovo, bolinho)
const ADDON_INGREDIENTS = {
  suco: [
    { name: 'Couve', qty: 0.030, unit: 'kg', category: 'vegetal', supplier: 'Hortifruti Pina' },
    { name: 'Maçã verde', qty: 0.100, unit: 'kg', category: 'fruta', supplier: 'Hortifruti Pina' },
    { name: 'Gengibre', qty: 0.005, unit: 'kg', category: 'tempero', supplier: 'Hortifruti Pina' },
    { name: 'Limão', qty: 0.020, unit: 'kg', category: 'fruta', supplier: 'Hortifruti Pina' },
    { name: 'Garrafinha 300ml', qty: 1, unit: 'un', category: 'embalagem', supplier: 'Embalax' },
  ],
  bolinho: KIT_INGREDIENTS.KIT_008,
  sopa:    KIT_INGREDIENTS.KIT_007,
  ovo: [
    { name: 'Ovo cozido', qty: 1, unit: 'un', category: 'proteina', supplier: 'Granja Pina' },
    { name: 'Saquinho selado pp', qty: 1, unit: 'un', category: 'embalagem', supplier: 'Embalax' },
  ],
};

// Mapping interno: addon.id → ingredients
function getAddonIngredients(addonId) {
  return ADDON_INGREDIENTS[addonId] || [];
}

// ── Função principal: lista por horizonte ──────────────────────────────────
const HORIZONS = {
  amanha:        { label: 'Amanhã',         start: 1, end: 1 },
  esta_semana:   { label: 'Esta semana',    start: 1, end: 7 },
  proxima_semana:{ label: 'Próxima semana', start: 8, end: 14 },
};

export function getHorizons() {
  return HORIZONS;
}

export function getPurchaseList(horizon = 'amanha') {
  const cfg = HORIZONS[horizon] || HORIZONS.amanha;
  const orders = getOrders().filter(o => o.dayOffset >= cfg.start && o.dayOffset <= cfg.end);

  // Conta demanda por kit + por addon
  const kitDemand = {};
  const addonDemand = {};
  orders.forEach(o => {
    kitDemand[o.kitId] = (kitDemand[o.kitId] || 0) + 1;
    (o.addons || []).forEach(a => {
      addonDemand[a.id] = (addonDemand[a.id] || 0) + 1;
    });
  });

  // Agrega ingredientes
  const aggregate = {}; // key: name+unit, value: { name, qty, unit, category, supplier, fromKits }
  function add(ing, multiplier, source) {
    const key = ing.name + '|' + ing.unit;
    if (!aggregate[key]) {
      aggregate[key] = {
        name: ing.name,
        unit: ing.unit,
        category: ing.category,
        supplier: ing.supplier,
        qty: 0,
        sources: {},
      };
    }
    aggregate[key].qty += ing.qty * multiplier;
    aggregate[key].sources[source] = (aggregate[key].sources[source] || 0) + multiplier;
  }

  Object.entries(kitDemand).forEach(([kitId, count]) => {
    (KIT_INGREDIENTS[kitId] || []).forEach(ing => add(ing, count, kitId));
  });
  Object.entries(addonDemand).forEach(([addonId, count]) => {
    getAddonIngredients(addonId).forEach(ing => add(ing, count, addonId));
  });

  const items = Object.values(aggregate).map(a => ({
    ...a,
    qty: roundQty(a.qty, a.unit),
  }));

  // Agrupa por categoria
  const byCategory = {};
  items.forEach(it => {
    if (!byCategory[it.category]) byCategory[it.category] = [];
    byCategory[it.category].push(it);
  });
  Object.values(byCategory).forEach(arr => arr.sort((a, b) => b.qty - a.qty));

  // Agrupa por fornecedor
  const bySupplier = {};
  items.forEach(it => {
    if (!bySupplier[it.supplier]) bySupplier[it.supplier] = [];
    bySupplier[it.supplier].push(it);
  });

  // Métricas
  const totalItems = items.length;
  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((s, o) => s + o.total, 0);
  const totalUnits = items.reduce((s, it) => s + (it.unit === 'un' ? it.qty : 0), 0);

  return {
    horizon,
    horizonLabel: cfg.label,
    horizonRange: cfg,
    items,
    byCategory,
    bySupplier,
    metrics: {
      totalItems,
      totalOrders,
      totalRevenue,
      totalUnits,
    },
  };
}

function roundQty(q, unit) {
  if (unit === 'un') return Math.ceil(q);
  if (unit === 'kg' || unit === 'l') return Math.round(q * 100) / 100;
  return Math.round(q * 100) / 100;
}

// ── Checklist operacional do dia (cozinha) ─────────────────────────────────
export function getKitchenChecklist() {
  const tomorrow = getPurchaseList('amanha');
  const checks = [];

  // Tarefas baseadas em quantidade de pedidos
  if (tomorrow.metrics.totalOrders > 0) {
    checks.push({
      id: 'mise',
      title: 'Mise en place',
      desc: `Separar e pesar ingredientes para ${tomorrow.metrics.totalOrders} marmitas`,
      time: '06:00',
      duration: '60min',
      priority: 'alta',
    });
    checks.push({
      id: 'cook',
      title: 'Preparo principal',
      desc: 'Cozinhar proteínas, arroz integral, vegetais',
      time: '07:00',
      duration: '120min',
      priority: 'alta',
    });
    checks.push({
      id: 'pack',
      title: 'Envase e selagem',
      desc: `Embalar ${tomorrow.metrics.totalOrders} marmitas em alumínio + etiqueta`,
      time: '09:30',
      duration: '60min',
      priority: 'alta',
    });
    checks.push({
      id: 'delivery',
      title: 'Saída para entrega',
      desc: 'Rota: Boa Viagem → Pina → Espinheiro → Madalena',
      time: '11:00',
      duration: '90min',
      priority: 'alta',
    });
  }
  checks.push({
    id: 'limpa',
    title: 'Limpeza da cozinha',
    desc: 'Higienização completa + sanitização das bancadas',
    time: '14:00',
    duration: '45min',
    priority: 'média',
  });
  checks.push({
    id: 'review',
    title: 'Conferir pedidos do dia seguinte',
    desc: 'Validar pedidos confirmados, atualizar ficha técnica',
    time: '16:30',
    duration: '20min',
    priority: 'média',
  });

  return checks;
}

// ── Agenda de entregas ─────────────────────────────────────────────────────
export function getDeliverySchedule(horizon = 'amanha') {
  const cfg = HORIZONS[horizon] || HORIZONS.amanha;
  const orders = getOrders().filter(o => o.dayOffset >= cfg.start && o.dayOffset <= cfg.end);

  // Agrupa por dia
  const byDay = {};
  orders.forEach(o => {
    const key = o.date.toDateString();
    if (!byDay[key]) byDay[key] = { date: o.date, orders: [] };
    byDay[key].orders.push(o);
  });

  return Object.values(byDay)
    .sort((a, b) => a.date - b.date)
    .map(d => ({
      date: d.date,
      label: d.date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' }),
      total: d.orders.length,
      revenue: d.orders.reduce((s, o) => s + o.total, 0),
      orders: d.orders,
    }));
}

// ── Alerts de estoque (mock — em produção viria do inventário) ─────────────
export function getStockAlerts() {
  return [
    { item: 'Embalagem alumínio M', status: 'baixo', stock: 80, recommended: 200, supplier: 'Embalax' },
    { item: 'Frango grelhado',      status: 'ok',    stock: 12, recommended: 8,   supplier: 'Açougue Boa Viagem', unit: 'kg' },
    { item: 'Couve',                status: 'crítico', stock: 0.5, recommended: 3, supplier: 'Hortifruti Pina', unit: 'kg' },
    { item: 'Sacola térmica kraft', status: 'baixo', stock: 12, recommended: 30, supplier: 'Embalax' },
  ];
}
